import { Col, Row as AntdRow } from 'antd';
import { Dictionary } from 'lodash';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as RsComponents } from 'react-select';
import styled from 'styled-components';
import { MemberType, Team, TeamMeta } from 'api/models';
import { Project } from 'api/project';
import { CreatableSelect, Option } from 'components/atoms/select';
import { DropdownIndicator } from 'components/atoms/select/custom-components';
import { VirtualizedMenuList } from 'components/molecules/assignee-picker/virtualised-list';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { Extra } from 'components/molecules/squad-picker';
import { AvatarOption } from 'components/molecules/squad-picker/picker';
import { Caption01 } from 'components/styles';
import { useOptionsCombinedForPlaceholderValue } from './options-for-placeholder';
import { usePlaceholderValueHelpers } from './placeholder-value-helper';
import { usePlaceholderValueSelectHelpers } from './use-placeholder-select-helper';

const Row = styled(AntdRow)`
  margin: var(--spacing-06) 0;
`;
const PlaceholderLabel = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
`;
const PlaceholderName = styled.div``;
const PlaceholderMeta = styled.div`
  ${Caption01};
  color: var(--gray-70);
`;
const MENU_HEIGHT = 300;

interface Props {
  placeholder: Team;
  project: Project;
  value?: Option<Extra> | null;
  placeholderMeta: Dictionary<TeamMeta>;
  onChange: (placeholder: Team, value?: Option<Extra>) => void;
}
export const PlaceholderRow: FC<Props> = ({
  placeholder,
  project,
  value,
  onChange,
  placeholderMeta,
}) => {
  const { isValidNewOptions, noOptionsMessage } =
    usePlaceholderValueSelectHelpers({
      placeholder,
      project,
    });
  const { options } = useOptionsCombinedForPlaceholderValue({
    placeholder,
    project,
  });
  const { getResponsibleCompany } = usePlaceholderValueHelpers();
  const responsibleCompany = useMemo(
    () => getResponsibleCompany(placeholder),
    [getResponsibleCompany, placeholder],
  );
  const onPlaceholderValueChange = useCallback(
    (selected) => {
      onChange(placeholder, selected);
    },
    [onChange, placeholder],
  );
  const containerDivRef = useRef<HTMLDivElement>(null);
  const [selectMenuPlacement, setSelectMenuPlacement] = useState('bottom');
  const onMenuOpen = useCallback(() => {
    const bottom = containerDivRef.current?.getBoundingClientRect().bottom;
    const windowHeight = window.innerHeight;

    if (bottom && bottom + MENU_HEIGHT > windowHeight) {
      setSelectMenuPlacement('top');
    } else {
      setSelectMenuPlacement('bottom');
    }
  }, []);

  return (
    <Row>
      <Col xs={12}>
        <PlaceholderLabel>
          <PlaceholderAvatar
            isCustomerRole={placeholder.teamType === MemberType.External}
            name={placeholder.teamName}
            companyLogoUrl={responsibleCompany?.companyLogoUrl}
          />
          <PlaceholderName>
            <span>{placeholder.teamName}</span>
            {(placeholderMeta[placeholder.teamId]?.totalTask ?? 0) > 0 ? (
              <PlaceholderMeta>
                <FormattedMessage
                  id="projects.create.team_details.placeholder.task.assigned.label"
                  values={{
                    count: placeholderMeta[placeholder.teamId]?.totalTask,
                  }}
                />
              </PlaceholderMeta>
            ) : null}
          </PlaceholderName>
        </PlaceholderLabel>
      </Col>
      <Col xs={12} ref={containerDivRef}>
        <CreatableSelect
          type="user"
          isClearable
          noOptionsMessage={noOptionsMessage}
          placeholder={
            <FormattedMessage id="projects.create.team_details.placeholders.input.placeholder" />
          }
          isValidNewOption={isValidNewOptions}
          options={options}
          value={value}
          onChange={onPlaceholderValueChange}
          grouped
          maxMenuHeight={MENU_HEIGHT}
          components={{
            MenuList: VirtualizedMenuList,
            Option: AvatarOption,
            DropdownIndicator,
            IndicatorSeparator: RsComponents.IndicatorSeparator,
          }}
          menuPortalTarget={document.body}
          menuPlacement={selectMenuPlacement}
          onMenuOpen={onMenuOpen}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 'var(--zindex-popover-low)',
            }),
          }}
        />
      </Col>
    </Row>
  );
};
